import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { appName } from "../../../../app.json";
import { ReadData } from "../../../../providers/Patient.Provider";
import LoadingSpinner from "../../../../partials/loading";
import DrugChartList from "./DrugChartList";
import { LIST, CREATE, INTAKE } from "../../../../queries/drug-chart.query";
import { useQuery, useMutation } from "@apollo/react-hooks";
import DrugChartForm from "./DrugChartForm";
import DrugIntakeForm from "./DrugIntake";
import DrugChartDetail from "./DrugChartDetail";
import { toast } from "react-toastify";
import { CleanMessage } from "../../../../util/app";

function DrugChart() {
    // local state
    const [newDrugChart, setNewDrugChart] = useState(false);
    const [drugChart, setDrugChart] = useState(null);
    const [addIntake, setAddIntake] = useState(null);

    // local data
    const patient = ReadData();

    // query
    const { loading, data } = useQuery(LIST, {
        variables: {
            id: patient.lastAdmission || patient.id
        },
        onError: e => toast.error(CleanMessage(e.message))
    });
    // New
    const [PostCreateFunc, { loading: cLoading }] = useMutation(CREATE, {
        update(caches, { data: { RecordNewDrugChart } }) {
            const { GetDrugCharts } = caches.readQuery({
                query: LIST,
                variables: {
                    id: patient.lastAdmission || patient.id
                }
            });
            GetDrugCharts.docs.unshift(RecordNewDrugChart.doc);
            caches.writeQuery({
                query: LIST,
                variables: {
                    id: patient.lastAdmission || patient.id
                },
                data: { GetDrugCharts }
            });
        },
        onError: e => toast.error(CleanMessage(e.message)),
        onCompleted: d => {
            if (d.RecordNewDrugChart) {
                toast.success(d.RecordNewDrugChart.message);
            }
        }
    });

    const [intakeFunc, { loading: iLoading }] = useMutation(INTAKE, {
        onError: e => toast.error(CleanMessage(e.message)),
        onCompleted: d => {
            if (d.RecordDrugAdministration) {
                toast.success(d.RecordDrugAdministration.message);
                setNewDrugChart(false);
                setDrugChart(null);
                scrollTop();
                setAddIntake(null);
            }
        }
    });

    /**
     * A function that scrolls the page to top
     */
    function scrollTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    return (
        <>
            <Helmet>
                <title>Patient Drug Chart | {appName}</title>
            </Helmet>
            <div className="row justify-content-center">
                {newDrugChart && !drugChart && !addIntake && (
                    <div className="col md-12 fade-in">
                        <button
                            onClick={() => {
                                setNewDrugChart(false);
                                setDrugChart(null);
                                scrollTop();
                                setAddIntake(null);
                            }}
                            title="Back"
                            className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                        >
                            <i className="material-icons text-primary">keyboard_backspace</i>
                        </button>
                        Record new drug chart
                        <LoadingSpinner loading={cLoading} />
                        <DrugChartForm
                            onCancel={() => {
                                setNewDrugChart(false);
                                setDrugChart(null);
                                scrollTop();
                                setAddIntake(null);
                            }}
                            onSubmit={async item => {
                                await PostCreateFunc({
                                    variables: {
                                        model: {
                                            ...item,
                                            admission: patient.lastAdmission,
                                            patient: patient.id
                                        },
                                        admission: patient.lastAdmission
                                    }
                                });
                                setTimeout(() => {
                                    setNewDrugChart(false);
                                    setDrugChart(null);
                                    scrollTop();
                                    setAddIntake(null);
                                }, 500);
                            }}
                        />
                    </div>
                )}
                {!newDrugChart && !drugChart && !addIntake && (
                    <div className="col-md-12 fade-in">
                        <div className="card">
                            <div className="card-head">
                                <header>Drug Chart</header>
                                <div className="float-right mt-2 mr-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewDrugChart(true);
                                            setDrugChart(null);
                                            scrollTop();
                                            setAddIntake(null);
                                        }}
                                        className="mdl-button fade-in mdl-button--raised mdl-js-ripple-effect m-b-10 btn-circle"
                                    >
                                        <i className="material-icons text-primary">add</i> New Drug Chart
                                    </button>
                                </div>
                            </div>
                            <div className="card-body m-b-20" style={{ minHeight: "420px" }}>
                                <LoadingSpinner loading={loading} />
                                {!loading && (
                                    <DrugChartList
                                        className="fade-in"
                                        items={data && data.GetDrugCharts.docs}
                                        onView={item => {
                                            setNewDrugChart(false);
                                            setDrugChart(item);
                                            scrollTop();
                                            setAddIntake(null);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {!newDrugChart && drugChart && !addIntake && (
                    <div className="col md-12 fade-in">
                        <div className="card">
                            <div className="card-head">
                                <header>
                                    <button
                                        onClick={() => {
                                            setNewDrugChart(false);
                                            setDrugChart(null);
                                            scrollTop();
                                            setAddIntake(null);
                                        }}
                                        title="Back"
                                        className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                    >
                                        <i className="material-icons text-primary">keyboard_backspace</i>
                                    </button>
                                    Recorded Drug Chart Detail
                                </header>
                            </div>
                            <div className="card-body m-b-20">
                                {/* Detail */}
                                <DrugChartDetail
                                    item={drugChart}
                                    onIntake={item => {
                                        setAddIntake(item);
                                        setNewDrugChart(false);
                                        setDrugChart(null);
                                        scrollTop();
                                    }}
                                />
                            </div>
                            <div className="card-footer">
                                <button
                                    onClick={() => {
                                        setNewDrugChart(false);
                                        setDrugChart(null);
                                        scrollTop();
                                        setAddIntake(null);
                                    }}
                                    title="Back"
                                    className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                >
                                    <i className="material-icons text-primary">keyboard_backspace</i>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {!newDrugChart && !drugChart && addIntake && (
                    <div className="col md-12 fade-in">
                        <div className="card">
                            <div className="card-head">
                                <header>
                                    <button
                                        onClick={() => {
                                            setNewDrugChart(false);
                                            setDrugChart(null);
                                            setAddIntake(null);
                                            scrollTop();
                                        }}
                                        title="Back"
                                        className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                    >
                                        <i className="material-icons text-primary">keyboard_backspace</i>
                                    </button>
                                    Record Drug Administration
                                </header>
                            </div>
                            <div className="card-body m-b-20">
                                <LoadingSpinner loading={iLoading} />
                                <DrugIntakeForm
                                    onCancel={() => {
                                        setNewDrugChart(false);
                                        setDrugChart(null);
                                        setAddIntake(null);
                                        scrollTop();
                                    }}
                                    onSubmit={async update => {
                                        const { id, prescriptionId } = addIntake;
                                        await intakeFunc({
                                            variables: {
                                                id,
                                                update: { ...update, prescriptionId }
                                            }
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default DrugChart;
